import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface CompleteRunModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (start: number, end: number, increment: number) => void;
  defaultStart?: number;
  defaultEnd?: number;
  defaultIncrement?: number;
}

const CompleteRunModal: React.FC<CompleteRunModalProps> = ({
  open,
  onClose,
  onSubmit,
  defaultStart = 10,
  defaultEnd = 1000,
  defaultIncrement = 10,
}) => {
  const [workflowStart, setWorkflowStart] = useState<number>(defaultStart);
  const [workflowEnd, setWorkflowEnd] = useState<number>(defaultEnd);
  const [increment, setIncrement] = useState<number>(defaultIncrement);
  const [errors, setErrors] = useState<{ start?: string; end?: string; increment?: string }>({});

  const validate = (): boolean => {
    const newErrors: { start?: string; end?: string; increment?: string } = {};

    if (workflowStart < 1) {
      newErrors.start = 'Start must be at least 1';
    }

    if (workflowEnd < workflowStart) {
      newErrors.end = 'End must be greater than or equal to start';
    }

    if (increment < 1) {
      newErrors.increment = 'Increment must be at least 1';
    }

    if (increment > (workflowEnd - workflowStart)) {
      newErrors.increment = 'Increment must not exceed the range';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(workflowStart, workflowEnd, increment);
      onClose();
    }
  };

  const handleStartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setWorkflowStart(value);
      setErrors({ ...errors, start: undefined });
    }
  };

  const handleEndChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setWorkflowEnd(value);
      setErrors({ ...errors, end: undefined });
    }
  };

  const handleIncrementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setIncrement(value);
      setErrors({ ...errors, increment: undefined });
    }
  };

  const getNumberOfRuns = (): number => {
    if (workflowEnd < workflowStart || increment < 1) return 0;
    return Math.floor((workflowEnd - workflowStart) / increment) + 1;
  };

  const isValid = (): boolean => {
    return (
      workflowStart >= 1 &&
      workflowEnd >= workflowStart &&
      increment >= 1 &&
      increment <= (workflowEnd - workflowStart)
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Run Configuration</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure the workflow task range for sequential optimization runs.
            Parameters will be automatically adjusted based on each task count.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Workflow Tasks Start"
                type="number"
                value={workflowStart}
                onChange={handleStartChange}
                fullWidth
                required
                error={!!errors.start}
                helperText={errors.start || 'Starting number of workflow tasks'}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Workflow Tasks End"
                type="number"
                value={workflowEnd}
                onChange={handleEndChange}
                fullWidth
                required
                error={!!errors.end}
                helperText={errors.end || 'Ending number of workflow tasks'}
                inputProps={{ min: workflowStart }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Increment Range"
                type="number"
                value={increment}
                onChange={handleIncrementChange}
                fullWidth
                required
                error={!!errors.increment}
                helperText={errors.increment || 'Step size for each run (e.g., 10 means: 10, 20, 30, ...)'}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  color: 'primary.contrastText',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Summary
                </Typography>
                <Typography variant="body2">
                  Total runs: <strong>{getNumberOfRuns()}</strong>
                </Typography>
                <Typography variant="body2">
                  Task counts: {workflowStart}, {workflowStart + increment}, ..., {workflowEnd}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={<PlayArrowIcon />}
          disabled={!isValid()}
        >
          Start Complete Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteRunModal;

